import { useEffect } from 'react';
import { useRouter } from 'next/router';

import {
  AdornedInput,
  Alert,
  Anchor,
  Box,
  FormField,
  FormHelp,
  Heading,
  Label,
  Paragraph,
  Popover,
  Text,
  TriggerText,
} from '@zillow/constellation-10';

import { logError } from '@zg-rentals/log-error';
import { useQueryClient } from '@zg-rentals/react-query5';
import {
  useRentalManagerPropertyAddressRentZestimateWebApiHandler_rentZestimate,
  useRentalManagerUserPropertyFloorPlanEditV2WebApiHandler_price,
} from '@zg-rentals/ts-rental-manager-api-client-react-query';

import { useGetListingDetails } from '../../../hooks/queries/properties';
import { useFieldSave } from '../../../hooks/useFieldSave';
import { usePropertyEditMutation } from '../../../hooks/usePropertyEditMutation';
import { getRentZestimateMeta, getRentZestimateParams } from '../../../hooks/usePrefetchMonthlyRent';
import accessUtils from '../../../utils/accessUtils';
import numberUtils from '../../../utils/numberUtils';

import { emitLearnMoreEvent, EVENT_IDS } from '../../../analytics/events/singleFamilyTotalMonthlyPrice';
import { useAuth } from '../../AuthProvider';

const inputName = 'price';
const rentZestimateLink = 'https://www.zillow.com/z/rental/what-is-a-rent-zestimate/';

// Query tags to refetch after a rent edit. Add any query tag that displays this rent here. Only the
// per-group tables can be scoped to a group; the rest (e.g. the flat units lists) have no group tag and
// always refetch.
const GROUP_SCOPED_TAG_LIST = ['getPropertyTableUnits', 'getListingPropertyUnits'];
const INVALIDATION_TAG_LIST = [...GROUP_SCOPED_TAG_LIST, 'getPropertyTableUnitsList', 'getListingUnitsList'];

// Whether a query with these tags shows this rent. Scoped to one group only when the group is known.
export const isRentDisplayQuery = (tags: ReadonlyArray<string>, groupId?: string) => {
  const isUnitsTableQuery = INVALIDATION_TAG_LIST.some((tag) => tags.includes(tag));
  const isGroupScopedQuery = GROUP_SCOPED_TAG_LIST.some((tag) => tags.includes(tag));
  const matchesGroup = groupId && isGroupScopedQuery ? tags.includes(groupId) : true;

  return isUnitsTableQuery && matchesGroup;
};

const MonthlyRentFormField = ({
  isRelistingFieldset = false,
  listingAlias: listingAliasProp,
  groupId,
}: {
  isRelistingFieldset?: boolean;
  listingAlias?: string;
  groupId?: string;
}) => {
  const router = useRouter();
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const listingAlias = listingAliasProp ?? (router.query.listingAlias as string);
  const { data: listingDetails, isLoading: isListingDetailsLoading } = useGetListingDetails(listingAlias);

  const rentZestimateParams = getRentZestimateParams(listingDetails);

  const { data: rentZestimateData } = useRentalManagerPropertyAddressRentZestimateWebApiHandler_rentZestimate(
    rentZestimateParams,
    { enabled: !!rentZestimateParams.fullAddress, meta: getRentZestimateMeta(rentZestimateParams) },
  );

  const floorPlans = listingDetails?.floorPlans || [];
  const floorPlanId = floorPlans[0]?.id;

  // IMPORTANT: this field also mounts outside the wizard's loading gate (the units table "Add rent"
  // modal), where floorPlanId is absent until /details resolves. That caller holds its save until
  // the id exists; a listing that resolves without a floor plan can never save, so report it.
  useEffect(() => {
    if (isListingDetailsLoading || !listingDetails || floorPlanId) {
      return;
    }

    logError({
      error: new Error('No floor plan on a resolved listing, rent is not editable'),
      errorType: 'MonthlyRentFormFieldError',
      context: { listingAlias, api: 'details' },
    });
  }, [isListingDetailsLoading, listingDetails, floorPlanId, listingAlias]);

  const canAccessTotalMonthlyPriceWithFee = accessUtils.canAccessTotalMonthlyPriceWithFee({
    isEligible: listingDetails?.totalMonthlyPriceWithFeeNotice?.isEligible ?? false,
  });

  // Use formatPriceFromApi to resolve single price from lowPrice/highPrice
  const initialPrice = numberUtils.formatPriceFromApi(
    floorPlans[0]?.lowPrice?.toString(),
    floorPlans[0]?.highPrice?.toString(),
  );

  const updateMutation = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyFloorPlanEditV2WebApiHandler_price,
    listingAlias,
    onSuccess: () => {
      // Refetch the units table(s) that show this rent. Scope to this group when known;
      // otherwise (e.g. the review page) refetch every open table.
      queryClient.invalidateQueries({
        predicate: ({ meta }) => isRentDisplayQuery(meta?.tags ?? [], groupId),
      });
    },
    meta: {
      // invalidate in onSuccess
      context: { api: 'RentalManagerUserPropertyFloorPlanEditV2WebApiHandler_price', details: 'MonthlyRentFormField' },
    },
  });

  const priceField = useFieldSave({
    fieldName: inputName,
    initialValue: initialPrice,
    mutation: updateMutation,
    buildMutationVariables: (value) => (floorPlanId ? { postData: { floorPlanId, price: String(value) } } : null),
  });

  const renderZestimate = () => {
    const rentZestimateCurrency = numberUtils.asCurrency(Number(rentZestimateData?.rentZestimate ?? 0));
    return (
      <Popover
        body={
          <Text>
            The Rent Zestimate® is Zillow&apos;s best estimate of a home&apos;s value as a rental property.{' '}
            <Anchor
              href={rentZestimateLink}
              rel="noopener noreferrer"
              target="_blank"
              aria-label="Learn more about the Rent Zestimate®"
            >
              Learn more
            </Anchor>
            {' about the Rent Zestimate.'}
          </Text>
        }
        // oxlint-disable-next-line constellation-content/sentence-case
        header={<Heading level={2}>Rent Zestimate®</Heading>}
        trigger={<TriggerText css={{ fontWeight: 'regular' }}>Rent Zestimate®: {rentZestimateCurrency}</TriggerText>}
      />
    );
  };

  const label = canAccessTotalMonthlyPriceWithFee ? 'Base rent' : 'Monthly rent';

  const priceFieldErrorDescription =
    priceField.error && priceField.error.includes('Price')
      ? priceField.error.replaceAll('Price', label)
      : priceField.error;

  return (
    <>
      {!!rentZestimateData?.rentZestimate && !isRelistingFieldset && (
        <Paragraph
          css={{ marginBottom: '600' }}
        >{`The current Rent Zestimate for this property is: ${numberUtils.asCurrency(rentZestimateData?.rentZestimate ?? 0)}`}</Paragraph>
      )}
      <FormField
        data-testid="MonthlyRentFormField"
        required={true}
        label={
          <Box
            css={{
              display: 'flex',
              alignItems: 'center',
              gap: 'tighter',
              marginBlockEnd: 'tightest',
            }}
          >
            <Label>{label}</Label>
            {isRelistingFieldset && !!rentZestimateData?.rentZestimate && renderZestimate()}
          </Box>
        }
        control={
          <AdornedInput
            size="md"
            input={
              <AdornedInput.Input
                name={inputName}
                value={String(priceField.value || '')}
                onChange={(e) => priceField.onChange(e.target.value)}
                onBlur={priceField.onBlur}
              />
            }
            startAdornment={<AdornedInput.Adornment>$</AdornedInput.Adornment>}
            endAdornment={<AdornedInput.Adornment>/month</AdornedInput.Adornment>}
          />
        }
        error={!!priceField.error}
        description={<FormHelp>{priceFieldErrorDescription}</FormHelp>}
      />

      {canAccessTotalMonthlyPriceWithFee && (
        <Alert css={{ marginTop: '400', marginBottom: '400' }}>
          You&apos;ll have the opportunity later to itemize any fees for this listing.{' '}
          <Anchor
            target="_blank"
            rel="noopener noreferrer"
            href="https://www.zillow.com/rentals-network/pricing-transparency/"
            aria-label="Learn more about fee transparency regulations"
            onClick={() => {
              if (!user || !listingDetails) return;
              emitLearnMoreEvent({
                user,
                currentProperty: listingDetails,
                triggerObject: 'monthly_rent_and_security_deposit',
                triggerLocation: !listingDetails?.modularListingProgress.step?.includes('review')
                  ? 'posting_path'
                  : 'posting_path_review',
              });
            }}
            data-eventid={EVENT_IDS.LEARN_MORE}
          >
            Learn more
          </Anchor>{' '}
          about fee transparency regulations in your area.
        </Alert>
      )}
    </>
  );
};

export default MonthlyRentFormField;
